import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, ButtonSize } from "../../../../components/Button";
import * as bip39 from "bip39";
import { useWallet } from "../../../../hooks/useWallet";
import { useAuth } from "../../../../hooks/useAuth";
import { Seed } from "@breeztech/breez-sdk-spark/web";

const WORD_COUNT = 12;

interface RestoreWalletFormProps {}

export const RestoreWalletForm: React.FC<RestoreWalletFormProps> = () => {
  const navigate = useNavigate();
  const { pleb } = useAuth();
  const { restoreWallet } = useWallet();
  const [mnemonicWords, setMnemonicWords] = useState<string[]>(
    Array(WORD_COUNT).fill("")
  );
  const [invalidIndexes, setInvalidIndexes] = useState<number[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isRestoring, setIsRestoring] = useState<boolean>(false);

  const words = Array.from({ length: WORD_COUNT }, (_, i) => i + 1);

  const handleChangeWord = (index: number, value: string) => {
    const updated = [...mnemonicWords];
    updated[index] = value;
    setMnemonicWords(updated);
  };

  const handlePasteWord = (
    startIndex: number,
    event: React.ClipboardEvent<HTMLInputElement>
  ) => {
    event.preventDefault();
    const text = event.clipboardData.getData("text");
    if (!text) return;

    const pastedWords = text
      .split(/\s+/)
      .map((w) => w.trim().toLowerCase())
      .filter((w) => w.length > 0);

    if (pastedWords.length === 0) return;

    const updated = [...mnemonicWords];
    for (let i = 0; i < pastedWords.length && startIndex + i < WORD_COUNT; i++) {
      updated[startIndex + i] = pastedWords[i];
    }

    setMnemonicWords(updated);
    setInvalidIndexes([]);
    setErrorMessage(null);
  };

  const handleRestore = () => {
    const english = bip39.wordlists.english as string[];

    const trimmedWords = mnemonicWords.map((w) => w.trim().toLowerCase());
    const invalid: number[] = [];

    trimmedWords.forEach((word, idx) => {
      if (!word || !english.includes(word)) {
        invalid.push(idx);
      }
    });

    if (invalid.length > 0) {
      setInvalidIndexes(invalid);
      setErrorMessage(
        "Please fill all 12 words with valid BIP39 seed words."
      );
      return;
    }

    setInvalidIndexes([]);
    setErrorMessage(null);

    const seed = { type: 'mnemonic', mnemonic: trimmedWords.join(" ") } as Seed;
    if(seed && pleb) {
      setIsRestoring(true);
      restoreWallet(pleb, seed)
        .then(() => {
          navigate("/home/wallet");
        })
        .catch((error) => {
          console.error('Error restoring wallet:', error);
          setErrorMessage("Failed to restore wallet. Please try again.");
        })
        .finally(() => {
          setIsRestoring(false);
        });
    }
  };

  return (
    <>
      {errorMessage && (
        <div className="text-center text-sm text-brand-red">
          {errorMessage}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        {words.map((index) => (
          <div key={index} className="flex flex-col gap-1">
            <div className="relative w-full">
              <span className="absolute inset-y-0 left-3 flex items-center text-xs opacity-60">
                {index}
              </span>
              <input
                type="text"
                className={`w-full rounded-full bg-brand-bg-4 pl-8 pr-3 py-2 text-sm outline-none border ${
                  invalidIndexes.includes(index - 1)
                    ? "border-brand-red focus:border-brand-red"
                    : "border-transparent focus:border-brand-orange"
                }`}
                placeholder="Word"
                autoComplete="off"
                value={mnemonicWords[index - 1]}
                onChange={(e) => handleChangeWord(index - 1, e.target.value)}
                onPaste={(e) => handlePasteWord(index - 1, e)}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4">
        <Button size={ButtonSize.large} onClick={handleRestore} loading={isRestoring}>
          Restore Wallet
        </Button>
      </div>
    </>
  );
};
